import * as THREE from 'three';

/**
 * The one material the whole diorama uses.
 *
 * There are no lights and no normals anywhere in this scene: shading is baked
 * into a per-vertex colour attribute when the geometry is built. That removes
 * the entire lighting path from the shader, which is the cheapest lighting
 * there is, and it is also how games of the era actually did it.
 *
 * Two era-accurate distortions are available but off by default. They are pure
 * styling and cost a little arithmetic; the low render resolution is doing the
 * real work.
 */
export interface PsxOptions {
  /** Snap vertices to a grid, like a console with no sub-pixel precision. */
  jitter?: boolean;
  /** Skip perspective correction on UVs, so textures swim on big surfaces. */
  affine?: boolean;
  fogColor?: number;
  fogNear?: number;
  fogFar?: number;
  /**
   * Discard fragments beyond |worldX|, so a train can vanish into a tunnel
   * mouth instead of sliding out across open space beside the diorama. Use a
   * separate material instance for the geometry that needs it: it costs one
   * extra shader program and no extra draw calls.
   */
  clipX?: number;
}

const vertexShader = /* glsl */ `
attribute vec3 aColor;

uniform vec2 uJitterGrid;

varying vec2 vUv;
varying vec3 vColor;
varying float vFogDepth;
varying float vW;

#ifdef PSX_CLIP_X
  varying float vWorldX;
#endif

void main() {
  vColor = aColor;

  #ifdef PSX_CLIP_X
    vWorldX = (modelMatrix * vec4(position, 1.0)).x;
  #endif

  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vFogDepth = -mvPosition.z;
  gl_Position = projectionMatrix * mvPosition;

  #ifdef PSX_JITTER
    // Quantise the clip-space position onto a virtual screen grid. Dividing by
    // w first puts us in NDC; multiplying back keeps the perspective divide the
    // GPU is about to do honest.
    vec2 ndc = gl_Position.xy / gl_Position.w;
    gl_Position.xy = floor(ndc * uJitterGrid) / uJitterGrid * gl_Position.w;
  #endif

  vW = gl_Position.w;

  #ifdef PSX_AFFINE
    // The GPU interpolates varyings perspective-correctly, dividing by w.
    // Pre-multiplying by w cancels that out, leaving plain linear interpolation
    // across the triangle in screen space - the era's texture swim.
    vUv = uv * gl_Position.w;
  #else
    vUv = uv;
  #endif
}
`;

const fragmentShader = /* glsl */ `
uniform sampler2D uMap;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uClipX;

varying vec2 vUv;
varying vec3 vColor;
varying float vFogDepth;
varying float vW;

#ifdef PSX_CLIP_X
  varying float vWorldX;
#endif

void main() {
  #ifdef PSX_CLIP_X
    if (abs(vWorldX) > uClipX) discard;
  #endif

  #ifdef PSX_AFFINE
    vec2 uvCoord = vUv / vW;
  #else
    vec2 uvCoord = vUv;
  #endif

  vec4 texel = texture2D(uMap, uvCoord);
  vec3 color = texel.rgb * vColor;

  float fog = smoothstep(uFogNear, uFogFar, vFogDepth);
  gl_FragColor = vec4(mix(color, uFogColor, fog), 1.0);
}
`;

export interface PsxMaterial extends THREE.ShaderMaterial {
  /** Toggle a styling flag and recompile. Used by the hidden debug panel. */
  setFlag(flag: 'jitter' | 'affine', on: boolean): void;
  /**
   * Fog distances are view depths from the camera, so they can only be chosen
   * once the framing is known - see Viewer.frame's return value.
   */
  setFog(near: number, far: number): void;
}

export function createPsxMaterial(
  map: THREE.Texture,
  options: PsxOptions = {},
): PsxMaterial {
  const {
    jitter = false,
    affine = false,
    fogColor = 0x0b0d12,
    fogNear = 30,
    fogFar = 70,
    clipX,
  } = options;

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uMap: { value: map },
      uFogColor: { value: new THREE.Color(fogColor) },
      uFogNear: { value: fogNear },
      uFogFar: { value: fogFar },
      // Half the render buffer's resolution: coarse enough to see, fine enough
      // that geometry does not tear itself apart.
      uJitterGrid: { value: new THREE.Vector2(160, 160) },
      uClipX: { value: clipX ?? 1e9 },
    },
    defines: clipX === undefined ? {} : { PSX_CLIP_X: '' },
  }) as PsxMaterial;

  material.setFlag = (flag, on) => {
    const key = flag === 'jitter' ? 'PSX_JITTER' : 'PSX_AFFINE';
    if (on) material.defines[key] = '';
    else delete material.defines[key];
    material.needsUpdate = true;
  };

  material.setFog = (near, far) => {
    material.uniforms.uFogNear.value = near;
    material.uniforms.uFogFar.value = far;
  };

  material.setFlag('jitter', jitter);
  material.setFlag('affine', affine);

  return material;
}
